/**
 * Phase 3a — Background GPS adapter.
 *
 * Targets two implementations:
 *   - Web / Capacitor foreground: navigator.geolocation watchPosition (current).
 *   - Capacitor native + `@capacitor-community/background-geolocation`
 *     when the plugin is installed and the device is iOS / Android.
 *
 * Backend contract is **unchanged**:
 *   POST /api/driver/location?lat=&lng=&accuracy_m=&speed_kph=&heading_deg=&booking_id=
 *
 * Public API:
 *   start({ token, apiBase, intervalMs?, bookingId? }) -> Promise<stopFn>
 *   stop()  // also returned by start()
 *
 * Plugin choice rationale:
 *   `@capacitor-community/background-geolocation` (MIT) is the most actively
 *   maintained Capacitor 7-compatible background GPS plugin. We dynamic-import
 *   it so the web bundle stays plugin-free; native bundles include it via the
 *   normal Capacitor `cap sync` flow.
 */
import axios from "axios";
import { isNative } from "./platform";

let _stopForeground = null;
let _stopNative = null;

async function _pingOnce(apiBase, token, coords, bookingId) {
  try {
    const params = new URLSearchParams({
      lat: String(coords.latitude),
      lng: String(coords.longitude),
    });
    if (coords.accuracy != null) params.set("accuracy_m", String(coords.accuracy));
    if (coords.speed != null) params.set("speed_kph", String((coords.speed || 0) * 3.6));
    if (coords.heading != null) params.set("heading_deg", String(coords.heading));
    if (bookingId) params.set("booking_id", bookingId);
    await axios.post(`${apiBase}/api/driver/location?${params.toString()}`, null, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: () => true,
    });
  } catch {
    // swallow — breadcrumbs are best-effort
  }
}

async function _startForeground({ token, apiBase, intervalMs = 30000, bookingId }) {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return () => {};
  }
  let timer = null;
  const ping = () => {
    navigator.geolocation.getCurrentPosition(
      (pos) => _pingOnce(apiBase, token, pos.coords, bookingId),
      () => {},
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  };
  ping();
  timer = setInterval(ping, intervalMs);
  return () => clearInterval(timer);
}

async function _startNative({ token, apiBase, bookingId }) {
  let plugin;
  try {
    const mod = await import("@capacitor-community/background-geolocation");
    plugin = mod?.BackgroundGeolocation;
  } catch {
    plugin = null;
  }
  if (!plugin) {
    // Plugin not yet installed in this native build — fall back to foreground
    // so we don't break tracking entirely.
    return _startForeground({ token, apiBase, bookingId });
  }
  const watcherId = await plugin.addWatcher(
    {
      backgroundMessage: "Tracking your location for active deliveries.",
      backgroundTitle: "Transport 247 Driver",
      requestPermissions: true,
      stale: false,
      distanceFilter: 20,
    },
    (location, error) => {
      if (error) return;
      if (!location) return;
      _pingOnce(apiBase, token, {
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        speed: location.speed,
        heading: location.bearing,
      }, bookingId);
    }
  );
  return () => plugin.removeWatcher({ id: watcherId });
}

export async function start({ token, apiBase, intervalMs = 30000, bookingId } = {}) {
  if (!token || !apiBase) throw new Error("backgroundGps.start: token + apiBase required");
  // Stop any previous instances.
  await stop();
  if (await isNative()) {
    _stopNative = await _startNative({ token, apiBase, bookingId });
    return stop;
  }
  _stopForeground = await _startForeground({ token, apiBase, intervalMs, bookingId });
  return stop;
}

export async function stop() {
  if (_stopForeground) {
    try { _stopForeground(); } catch { /* ignore */ }
    _stopForeground = null;
  }
  if (_stopNative) {
    try { await _stopNative(); } catch { /* ignore */ }
    _stopNative = null;
  }
}
